import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import AppreciationForm from '../components/appreciation/AppreciationForm';
import { getAnalyticsSummary } from '../services/analyticsService';

const SendAppreciation = () => {
  const location = useLocation();
  const isFirstTime = location.state?.isFirstTime;
  const [showFirstTimeGuide, setShowFirstTimeGuide] = useState(false);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    if (isFirstTime) {
      setShowFirstTimeGuide(true);
    }
  }, [isFirstTime]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoadingStats(true);
        const [weekData, monthData] = await Promise.all([
          getAnalyticsSummary('1w'),
          getAnalyticsSummary('1m')
        ]);
        setWeeklyStats(weekData);
        setMonthlyStats(monthData);
      } catch (error) {
        console.error('Error fetching recognition stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStats();
  }, []);

  return (
    <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      {showFirstTimeGuide && (
        <div className="mb-8 bg-indigo-50 border-l-4 border-indigo-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-indigo-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-indigo-800">First Appreciation</h3>
              <div className="mt-2 text-sm text-indigo-700">
                <p>Send your first appreciation to recognize a colleague for their hard work or contribution. Here's how:</p>
                <ol className="list-decimal list-inside mt-2 space-y-1">
                  <li>Enter a colleague's name in the recipient field</li>
                  <li>Write a sincere appreciation message</li>
                  <li>Tag it with relevant company values</li>
                  <li>Click "Send Appreciation"</li>
                </ol>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => setShowFirstTimeGuide(false)}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Appreciation</h1>
        <p className="mt-2 text-sm text-gray-600">Acknowledge your colleagues for their outstanding contributions.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <AppreciationForm />
        </div>

        <div className="lg:col-span-1 space-y-6">
          {/* Suggestions Card */}
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-sm font-medium text-gray-900">Recognition Suggestions</h2>
            </div>
            <div className="px-4 py-3">
              <div className="space-y-2">
                <button className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-sm text-gray-700 transition">
                  Who demonstrated exceptional problem-solving?
                </button>
                <button className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-sm text-gray-700 transition">
                  Who exemplified our company values recently?
                </button>
                <button className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-md text-sm text-gray-700 transition">
                  Who went above and beyond for the team?
                </button>
              </div>
            </div>
          </div>

          {/* Recognition Stats */}
          <div className="bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-sm font-medium text-gray-900">Company Recognition Stats</h2>
            </div>
            <div className="px-6 py-4">
              {loadingStats ? (
                <div className="text-center py-4 text-sm text-gray-500">Loading stats...</div>
              ) : (
                <dl className="grid grid-cols-2 gap-4">
                  <div>
                    <dt className="text-xs font-medium text-gray-500 truncate">This Week</dt>
                    <dd className="mt-1 text-2xl font-semibold text-gray-900">
                      {weeklyStats?.total_appreciations || 0}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500 truncate">This Month</dt>
                    <dd className="mt-1 text-2xl font-semibold text-gray-900">
                      {monthlyStats?.total_appreciations || 0}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500 truncate">Active Users</dt>
                    <dd className="mt-1 text-2xl font-semibold text-gray-900">
                      {weeklyStats?.active_users || 0}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium text-gray-500 truncate">Participation</dt>
                    <dd className="mt-1 text-2xl font-semibold text-gray-900">
                      {weeklyStats?.participation_rate || 0}%
                    </dd>
                  </div>
                </dl>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendAppreciation;